import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class TokenExpirationService {
  private expirationCheckInterval: Subscription | null = null;
  private timerUpdateInterval: Subscription | null = null;
  private currentExpirationTime: number | null = null;
  private readonly CHECK_INTERVAL = 30 * 1000; // Check every 30 seconds
  private readonly TIMER_UPDATE_INTERVAL = 1000; // Update timer display every second
  private readonly WARNING_TIME = 2 * 60 * 1000; // 2 minutes in milliseconds

  // Observable for time remaining (in seconds)
  public timeRemaining$ = new BehaviorSubject<number | null>(null);
  public showWarning$ = new BehaviorSubject<boolean>(false);
  // Observable for notifications
  public notification$ = new BehaviorSubject<{ message: string; type: 'error' | 'success' } | null>(null);

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  /**
   * Decode JWT token to get expiration time
   */
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Get token expiration time
   */
  private getTokenExpiration(token: string): number | null {
    const decoded = this.decodeToken(token);
    if (decoded && decoded.exp) {
      const expirationTimeMs = decoded.exp * 1000; // Convert to milliseconds
      const expirationTimeSeconds = decoded.exp; // Already in seconds
      const expirationDate = new Date(expirationTimeMs);
      
    //   console.log('=== Token Expiration Details ===');
    //   console.log('Decoded Token:', decoded);
    //   console.log('Token Expiration (Unix timestamp in seconds):', expirationTimeSeconds);
    //   console.log('Total Expiration Time (milliseconds):', expirationTimeMs);
    //   console.log('Total Expiration Time (seconds):', expirationTimeSeconds);
    //   console.log('Expiration Date:', expirationDate.toLocaleString());
    //   console.log('Expiration Date (ISO):', expirationDate.toISOString());
    //   console.log('================================');
      
      return expirationTimeMs;
    }
    return null;
  }

  /**
   * Calculate time remaining until expiration
   */
  private getTimeRemaining(expirationTime: number): number {
    const now = Date.now();
    return Math.max(0, Math.floor((expirationTime - now) / 1000)); // Return seconds
  }

  /**
   * Start monitoring token expiration
   * Checks token from sessionStorage every 30 seconds
   * Popup will only show when 2 minutes or less remain
   */
  startMonitoring(): void {
    // Clear any existing intervals
    // console.log("startMonitoring fuction");
    this.stopMonitoring();

    // Initialize: Hide popup and clear time remaining on start
    this.showWarning$.next(false);
    this.timeRemaining$.next(null);

    // Get token from sessionStorage directly
    const token = sessionStorage.getItem('token');
    if (!token) {
      return;
    }

    const expirationTime = this.getTokenExpiration(token);
    if (!expirationTime) {
      return;
    }

    this.currentExpirationTime = expirationTime;

    // Log current time and time remaining
    const now = Date.now();
    const nowSeconds = Math.floor(now / 1000);
    const expirationTimeSeconds = Math.floor(expirationTime / 1000);
    const initialTimeRemaining = this.getTimeRemaining(expirationTime);
    const initialTimeRemainingMs = initialTimeRemaining * 1000;
    const initialTimeRemainingMinutes = (initialTimeRemainingMs / 60).toFixed(2);
    
    // console.log('=== Token Monitoring Started ===');
    // console.log('Current Time (milliseconds):', now);
    // console.log('Current Time (seconds):', nowSeconds);
    // console.log('Current Time (Date):', new Date(now).toLocaleString());
    // console.log('Total Expiration Time (milliseconds):', expirationTime);
    // console.log('Total Expiration Time (seconds):', expirationTimeSeconds);
    // console.log('Expiration Time (Date):', new Date(expirationTime).toLocaleString());
    // console.log('Time Remaining (seconds):', initialTimeRemaining);
    // console.log('Time Remaining (milliseconds):', initialTimeRemainingMs);
    // console.log('Time Remaining (minutes):', initialTimeRemainingMinutes);
    // console.log('Time Remaining (formatted):', this.formatTime(initialTimeRemaining));
    // console.log('Warning Threshold (2 minutes):', this.WARNING_TIME, 'ms =', (this.WARNING_TIME / 1000), 'seconds');
    // console.log('Should Show Popup:', initialTimeRemainingMs <= this.WARNING_TIME && initialTimeRemainingMs > 0);
    // console.log('================================');

    // Do initial check but don't show popup immediately
    // Only check if token is expired, don't show warning yet
    if (initialTimeRemaining <= 0) {
      this.handleTokenExpired();
      return;
    }

    // Check token from sessionStorage every 30 seconds
    // This is the main check that determines if popup should be shown/hidden
    // console.log("its call interval check fuction");
    this.expirationCheckInterval = interval(this.CHECK_INTERVAL).subscribe(() => {
      // Get fresh token from sessionStorage
    //   console.log("call every 30 second");
      const freshToken = sessionStorage.getItem('token');
      if (!freshToken) {
        this.handleTokenExpired();
        return;
      }

      const freshExpirationTime = this.getTokenExpiration(freshToken);
      if (!freshExpirationTime) {
        this.handleTokenExpired();
        return;
      }

      // Update current expiration time
      this.currentExpirationTime = freshExpirationTime;
      
      // Log 30-second check details
      const now = Date.now();
      const nowSeconds = Math.floor(now / 1000);
      const expirationTimeSeconds = Math.floor(freshExpirationTime / 1000);
      const timeRemaining = this.getTimeRemaining(freshExpirationTime);
      const timeRemainingMs = timeRemaining * 1000;
    //   console.log('=== 30-Second Check ===');
    //   console.log('Current Time (milliseconds):', now);
    //   console.log('Current Time (seconds):', nowSeconds);
    //   console.log('Current Time (Date):', new Date(now).toLocaleString());
    //   console.log('Total Expiration Time (milliseconds):', freshExpirationTime);
    //   console.log('Total Expiration Time (seconds):', expirationTimeSeconds);
    //   console.log('Expiration Time (Date):', new Date(freshExpirationTime).toLocaleString());
    //   console.log('Time Remaining:', this.formatTime(timeRemaining), `(${timeRemaining} seconds)`);
    //   console.log('Time Remaining (milliseconds):', timeRemainingMs);
    //   console.log('Time Remaining (minutes):', (timeRemaining / 60).toFixed(2));
    //   console.log('Should Show Popup:', timeRemainingMs <= this.WARNING_TIME && timeRemainingMs > 0);
    //   console.log('=======================');
      
      // Check expiration and update warning visibility (show if <= 2 min, hide if > 2 min)
      this.checkTokenExpiration();
    });

    // Update timer display every second for smooth countdown
    // Only update time remaining when popup should be shown (<= 2 min)
    this.timerUpdateInterval = interval(this.TIMER_UPDATE_INTERVAL).subscribe(() => {
      if (this.currentExpirationTime) {
        this.updateTimerDisplay();
      }
    });
  }

  /**
   * Check token expiration and update state
   * This is called every 30 seconds to check token from sessionStorage
   * Only shows popup when 2 minutes or less remain
   */
  private checkTokenExpiration(): void {
    if (!this.currentExpirationTime) {
      return;
    }

    const timeRemaining = this.getTimeRemaining(this.currentExpirationTime);
    const timeRemainingMs = timeRemaining * 1000;

    // Check if token has expired
    if (timeRemaining <= 0) {
      this.handleTokenExpired();
      return;
    }

    // Show warning ONLY if 2 minutes (120 seconds) or less remaining
    // Hide warning if more than 2 minutes remaining
    if (timeRemainingMs <= this.WARNING_TIME && timeRemainingMs > 0) {
      // Show popup and update time remaining
      this.showWarning$.next(true);
      this.timeRemaining$.next(timeRemaining);
    } else {
      // Hide popup and clear time remaining when > 2 minutes
      this.showWarning$.next(false);
      this.timeRemaining$.next(null);
    }
  }

  /**
   * Update timer display (called every second for smooth countdown)
   * Only updates timer when popup is visible (<= 2 minutes remaining)
   */
  private updateTimerDisplay(): void {
    if (!this.currentExpirationTime) {
      return;
    }

    const timeRemaining = this.getTimeRemaining(this.currentExpirationTime);
    const timeRemainingMs = timeRemaining * 1000;

    // Check if token has expired
    if (timeRemaining <= 0) {
      this.handleTokenExpired();
      return;
    }

    // Only update timer display if popup should be shown (<= 2 minutes)
    // This ensures smooth countdown from 2:00 down to 0:00
    if (timeRemainingMs <= this.WARNING_TIME && timeRemainingMs > 0) {
      // Show popup if not already shown
      if (!this.showWarning$.value) {
        this.showWarning$.next(true);
      }
      // Update time remaining for countdown display
      this.timeRemaining$.next(timeRemaining);
    } else {
      // Hide popup if more than 2 minutes remaining
      if (this.showWarning$.value) {
        this.showWarning$.next(false);
        this.timeRemaining$.next(null);
      }
    }
  }

  /**
   * Handle token expiration
   */
  private handleTokenExpired(): void {
    this.stopMonitoring();
    this.showWarning$.next(false);
    this.timeRemaining$.next(null);
    
    // Show notification instead of alert
    this.notification$.next({
      message: 'Your session has expired. Please login again.',
      type: 'error'
    });
    
    // Logout user
    this.authService.logout();
  }

  /**
   * Stop monitoring token expiration
   */
  stopMonitoring(): void {
    if (this.expirationCheckInterval) {
      this.expirationCheckInterval.unsubscribe();
      this.expirationCheckInterval = null;
    }
    if (this.timerUpdateInterval) {
      this.timerUpdateInterval.unsubscribe();
      this.timerUpdateInterval = null;
    }
    this.currentExpirationTime = null;
    this.showWarning$.next(false);
    this.timeRemaining$.next(null);
  }

  /**
   * Format seconds to MM:SS format
   */
  formatTime(seconds: number): string {
    if (seconds <= 0) {
      return '00:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Refresh monitoring (call when token is updated)
   */
  refresh(): void {
    this.stopMonitoring();
    this.startMonitoring();
  }
}

